import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
  import {
    calculatePolygonCentroid,
    calculateBoundingBoxCenter,
    getLotLabelGap,
    LOT_NUMBER_FONT_SIZE,
    LOT_SIZE_FONT_SIZE,
    LOT_SIZE_DEFAULT_SCALE,
  } from "@/helpers/lotNumber/lotCentroidCalculator";
  import {
    buildQuadraticShapePath,
    convertSvgPathToEditableGeometry,
    getEdgeMidpoint,
    getQuadraticControlPoint,
    type CurvePoint,
  } from "@/helpers/masterplan/curvedLotShape";

  interface SvgElement {
    elementIndex: number;
    type: "rect" | "path" | "polygon" | "circle";
    id?: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    polygonPoints?: Array<{ x: number; y: number }>;
    rawAttributes: Record<string, string>;
  }

  interface Assignment {
    elementIndex: number;
    type: string;
    lotId: string;
    boundingBox: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    shapePath?: string;
    statusBarPath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    curvePoints?: Array<{ edgeIndex: number; x: number; y: number }>;
    lotNumberMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
    lotSizeMetadata?: {
      offsetX: number;
      offsetY: number;
      scale: number;
      rotation: number;
      displayed: boolean;
    };
  }

  interface Lot {
    lotId: string;
    lotTitle: string;
    lotStatus: string;
    stageId?: string;
    stageTitle?: string;
    lotPrice?: number;
    lotSize?: number;
  }

  interface Stage {
    _id: string;
    title: string;
    color?: string;
  }

  interface Point {
    x: number;
    y: number;
  }

  interface StageBoundary {
    points: Point[];
    curvePoints: CurvePoint[];
    centroid: Point | null;
  }

  interface StageLabelMeta {
    offsetX: number;
    offsetY: number;
    scale: number;
    rotation: number;
    displayed: boolean;
  }

  interface StageAssignmentResult {
    stageId: string;
    boundaryPath: string;
    boundaryPoints: Point[];
    curvePoints: CurvePoint[];
  }

  interface EnrichedAssignment extends Assignment {
    stageId?: string;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'StageDrawer',
  props: {
    svgFile: {},
    assignments: {},
    availableLots: {},
    stages: {},
    initialStageBoundaries: {},
    initialStageLabelMetadata: {}
  },
  emits: ["complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const STAGE_COLORS = [
    "#4a90e2",
    "#e24a4a",
    "#4aa84a",
    "#e2c34a",
    "#c34ae2",
    "#4ae2c3",
    "#e2874a",
    "#874ae2",
  ];

  const props = __props;

  const emit = __emit;

  const svgContainer = ref<HTMLDivElement | null>(null);
  const svgOverlay = ref<SVGSVGElement | null>(null);
  const svgContent = ref("");
  const overlayViewBox = ref("0 0 1000 1000");

  const stageLabelTextRefs = ref<Record<string, SVGTextElement>>({});
  const stageLabelBboxes = ref<
    Record<string, { x: number; y: number; width: number; height: number }>
  >({});

  const stageLabelPadX = computed(() => stageLabelFontSize.value * 0.1);
  const stageLabelPadY = computed(() => stageLabelFontSize.value * 0.05);
  const stageLabelPillRx = computed(() => stageLabelFontSize.value * 0.08);

  const measureStageLabelBboxes = async () => {
    await nextTick();
    const result: Record<
      string,
      { x: number; y: number; width: number; height: number }
    > = {};

    for (const [key, el] of Object.entries(stageLabelTextRefs.value)) {
      if (el && el.isConnected) {
        const bb = (el as SVGTextElement).getBBox();

        result[key] = { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
      }
    }

    stageLabelBboxes.value = result;
  };

  const zoomLevel = ref(1);
  const panX = ref(0);
  const panY = ref(0);
  const isDragging = ref(false);
  const wasDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const mouseDownPos = ref({ x: 0, y: 0 });

  const isDrawing = ref(false);
  const selectedStage = ref<Stage | null>(null);
  const draftPoints = ref<Point[]>([]);
  const draftCurvePoints = ref<CurvePoint[]>([]);
  const mouseViewPos = ref<Point>({ x: 0, y: 0 });
  const draggingPointIndex = ref(-1);
  const wasPointDragging = ref(false);
  const draggingCurveEdgeIndex = ref<number | null>(null);
  const curveDragStart = ref({ x: 0, y: 0 });
  const curveHandleWasDragged = ref(false);

  const calcCentroid = (points: Point[]): Point | null => {
    if (!points.length) return null;

    const x = points.reduce((s, p) => s + p.x, 0) / points.length;
    const y = points.reduce((s, p) => s + p.y, 0) / points.length;

    return { x, y };
  };

  const buildInitialBoundaries = (): Record<string, StageBoundary> => {
    if (!props.initialStageBoundaries?.length) return {};

    const result: Record<string, StageBoundary> = {};

    for (const boundary of props.initialStageBoundaries) {
      const geometry =
        boundary.boundaryPoints && boundary.boundaryPoints.length >= 3
          ? {
              points: boundary.boundaryPoints,
              curvePoints: boundary.curvePoints ?? [],
            }
          : convertSvgPathToEditableGeometry(boundary.boundaryPath, 0);

      if (!geometry || geometry.points.length < 3) continue;

      const points = geometry.points.map((point) => ({ ...point }));

      result[boundary.stageId] = {
        points,
        curvePoints: geometry.curvePoints.map((point) => ({ ...point })),
        centroid: calcCentroid(points),
      };
    }

    return result;
  };

  const stageBoundaries = ref<Record<string, StageBoundary>>(
    buildInitialBoundaries()
  );
  const stashedBoundary = ref<{
    stageId: string;
    boundary: StageBoundary;
  } | null>(null);

  const buildInitialStageLabelMetadata = (): Record<string, StageLabelMeta> => {
    if (!props.initialStageLabelMetadata?.length) return {};

    const result: Record<string, StageLabelMeta> = {};

    for (const {
      stageId,
      offsetX,
      offsetY,
      scale,
      rotation,
      displayed,
    } of props.initialStageLabelMetadata) {
      result[stageId] = { offsetX, offsetY, scale, rotation, displayed };
    }

    return result;
  };

  const stageLabelMetadata = ref<Record<string, StageLabelMeta>>(
    buildInitialStageLabelMetadata()
  );

  const stageLabelAdjustments = ref<StageLabelMeta>({
    offsetX: 0,
    offsetY: 0,
    scale: 1.8,
    rotation: 0,
    displayed: true,
  });

  const isInitializingLabel = ref(false);

  const svgStyle = computed(() => ({
    transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoomLevel.value})`,
    transformOrigin: "0 0",
  }));

  const hasBoundary = (stageId: string): boolean =>
    !!stageBoundaries.value[stageId];

  const getStageColor = (stage: Stage): string => {
    if (stage.color) return stage.color;

    const idx = props.stages.findIndex((s) => s._id === stage._id);

    return STAGE_COLORS[idx % STAGE_COLORS.length];
  };

  const getStageColorById = (stageId: string): string => {
    const stage = props.stages.find((s) => s._id === stageId);

    return stage ? getStageColor(stage) : "#4a90e2";
  };

  const getStageName = (stageId: string): string => {
    return props.stages.find((s) => s._id === stageId)?.title ?? stageId;
  };

  const getBoundaryStyle = (stageId: string) => {
    const color = getStageColorById(stageId);
    const isSelected = selectedStage.value?._id === stageId;

    return {
      fill: hexToRgba(color, isSelected ? 0.35 : 0.2),
      stroke: color,
      strokeWidth: isSelected ? "3" : "2",
      cursor: "pointer",
      pointerEvents: "all",
    };
  };

  const getBoundaryPath = (boundary: StageBoundary): string =>
    buildQuadraticShapePath(boundary.points, boundary.curvePoints) ?? "";

  const lotStageItems = computed(() =>
    props.assignments.map((a) => {
      const lot = props.availableLots.find((l) => l.lotId === a.lotId);
      const stageId = lot?.stageId ?? "";
      const stage = props.stages.find((s) => s._id === stageId);
      const stageColor = stage ? getStageColor(stage) : "#ccc";

      return {
        elementIndex: a.elementIndex,
        lotTitle: lot?.lotTitle ?? a.lotId,
        stageId,
        stageTitle: stage?.title ?? "—",
        stageColor,
      };
    })
  );

  const drawnBoundariesCount = computed(
    () => Object.keys(stageBoundaries.value).length
  );

  const hexToRgba = (hex: string, alpha: number): string => {
    const clean = hex.replace("#", "");
    const bigint = parseInt(
      clean.length === 3
        ? clean
            .split("")
            .map((c) => c + c)
            .join("")
        : clean,
      16
    );
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r},${g},${b},${alpha})`;
  };

  const computeCentroid = (points: Point[]): Point | null => {
    if (points.length === 0) return null;

    const x = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const y = points.reduce((sum, p) => sum + p.y, 0) / points.length;

    return { x, y };
  };

  const getSvgCoordsFromEvent = (e: MouseEvent): Point | null => {
    if (!svgOverlay.value) return null;

    const svg = svgOverlay.value;
    const pt = svg.createSVGPoint();

    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();

    if (!ctm) return null;

    const svgPt = pt.matrixTransform(ctm.inverse());

    return { x: svgPt.x, y: svgPt.y };
  };

  const stageLabelFontSize = computed(() => {
    if (!overlayViewBox.value) return 18;

    const parts = overlayViewBox.value.split(" ").map(Number);

    return Math.round(Math.min(parts[2], parts[3]) / 40) * 2;
  });

  const getStageLabelCenterX = (stageId: string, centroid: Point): number => {
    const meta = stageLabelMetadata.value[stageId];

    return centroid.x + (meta?.offsetX || 0);
  };

  const getStageLabelCenterY = (stageId: string, centroid: Point): number => {
    const meta = stageLabelMetadata.value[stageId];

    return centroid.y + (meta?.offsetY || 0);
  };

  const getStageLabelTransform = (stageId: string, centroid: Point): string => {
    const meta = stageLabelMetadata.value[stageId];
    const scale = meta?.scale ?? 1.8;
    const rotation = meta?.rotation ?? 0;

    if (scale === 1 && rotation === 0) return "";

    const x = centroid.x + (meta?.offsetX || 0);
    const y = centroid.y + (meta?.offsetY || 0);
    const transforms = [];

    transforms.push(`translate(${x} ${y})`);

    if (scale !== 1) transforms.push(`scale(${scale})`);

    if (rotation !== 0) transforms.push(`rotate(${rotation})`);

    transforms.push(`translate(${-x} ${-y})`);
    return transforms.join(" ");
  };

  const getLotNumberCenter = (assignment: Assignment): Point | null => {
    if (!assignment) return null;

    if (assignment.polygonPoints && assignment.polygonPoints.length > 0) {
      return calculatePolygonCentroid(assignment.polygonPoints);
    } else if (assignment.boundingBox) {
      return calculateBoundingBoxCenter(assignment.boundingBox);
    }

    return null;
  };

  const getLotNumberMetadata = (assignment: Assignment) => {
    if (!assignment || !assignment.lotNumberMetadata) return null;

    return assignment.lotNumberMetadata;
  };

  const shouldShowLotNumber = (assignment: Assignment): boolean => {
    if (!assignment.lotId) return false;

    const metadata = getLotNumberMetadata(assignment);

    if (!metadata) return true; // Show by default if no metadata

    return metadata.displayed !== false;
  };

  const getLotNumberCenterX = (assignment: Assignment): number => {
    const center = getLotNumberCenter(assignment);

    if (!center) return 0;

    const metadata = getLotNumberMetadata(assignment);

    return center.x + (metadata?.offsetX || 0);
  };

  const getLotNumberCenterY = (assignment: Assignment): number => {
    const center = getLotNumberCenter(assignment);

    if (!center) return 0;

    const metadata = getLotNumberMetadata(assignment);

    return center.y + (metadata?.offsetY || 0);
  };

  const getLotNumberTransform = (assignment: Assignment): string => {
    const center = getLotNumberCenter(assignment);

    if (!center) return "";

    const metadata = getLotNumberMetadata(assignment);

    if (!metadata || (metadata.scale === 1 && metadata.rotation === 0))
      return "";

    const x = center.x + (metadata.offsetX || 0);
    const y = center.y + (metadata.offsetY || 0);
    const transforms = [];

    transforms.push(`translate(${x} ${y})`);

    if (metadata.scale && metadata.scale !== 1) {
      transforms.push(`scale(${metadata.scale})`);
    }

    if (metadata.rotation && metadata.rotation !== 0) {
      transforms.push(`rotate(${metadata.rotation})`);
    }

    transforms.push(`translate(${-x} ${-y})`);
    return transforms.join(" ");
  };

  const getLotTitle = (assignment: Assignment): string => {
    const lot = props.availableLots.find((l) => l.lotId === assignment.lotId);

    return lot?.lotTitle || assignment.lotId;
  };

  const getLotSizeMetadata = (assignment: Assignment) =>
    assignment?.lotSizeMetadata || null;

  const getLotSize = (assignment: Assignment): string => {
    const lot = props.availableLots.find((l) => l.lotId === assignment.lotId);

    return lot?.lotSize === null || lot?.lotSize === undefined
      ? ""
      : `${lot.lotSize}m²`;
  };

  const shouldShowLotSize = (assignment: Assignment): boolean =>
    Boolean(getLotSize(assignment)) &&
    getLotSizeMetadata(assignment)?.displayed !== false;

  const getLotSizeCenterX = (assignment: Assignment): number => {
    const center = getLotNumberCenter(assignment);

    if (!center) return 0;

    return (
      center.x +
      (assignment.lotNumberMetadata?.offsetX || 0) +
      (assignment.lotSizeMetadata?.offsetX ?? 2)
    );
  };

  const getLotSizeCenterY = (assignment: Assignment): number => {
    const center = getLotNumberCenter(assignment);

    if (!center) return 0;

    const numberScale = assignment.lotNumberMetadata?.scale ?? 1;
    const sizeScale =
      assignment.lotSizeMetadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const labelGap = getLotLabelGap(numberScale, sizeScale);

    return (
      center.y +
      (assignment.lotNumberMetadata?.offsetY || 0) +
      labelGap +
      (assignment.lotSizeMetadata?.offsetY ?? 2)
    );
  };

  const getLotSizeTransform = (assignment: Assignment): string => {
    const metadata = getLotSizeMetadata(assignment);
    const scale = metadata?.scale ?? LOT_SIZE_DEFAULT_SCALE;
    const rotation = metadata?.rotation ?? 0;

    if (scale === 1 && rotation === 0) return "";

    const x = getLotSizeCenterX(assignment);
    const y = getLotSizeCenterY(assignment);
    const transforms = [`translate(${x} ${y})`];

    if (scale !== 1) transforms.push(`scale(${scale})`);

    if (rotation !== 0) transforms.push(`rotate(${rotation})`);

    transforms.push(`translate(${-x} ${-y})`);
    return transforms.join(" ");
  };

  const selectStage = (stage: Stage) => {
    if (isDrawing.value) {
      cancelDrawing();
    }

    selectedStage.value = selectedStage.value?._id === stage._id ? null : stage;

    if (selectedStage.value) {
      const meta = stageLabelMetadata.value[selectedStage.value._id];

      isInitializingLabel.value = true;
      stageLabelAdjustments.value = {
        offsetX: meta?.offsetX ?? 0,
        offsetY: meta?.offsetY ?? 0,
        scale: meta?.scale ?? 1.8,
        rotation: meta?.rotation ?? 0,
        displayed: meta?.displayed !== false,
      };
      nextTick(() => {
        isInitializingLabel.value = false;
      });
    }
  };

  const selectStageById = (stageId: string) => {
    const stage = props.stages.find((s) => s._id === stageId);

    if (stage) selectStage(stage);
  };

  const startDrawing = () => {
    if (!selectedStage.value) return;

    const existing = stageBoundaries.value[selectedStage.value._id];

    draftPoints.value = existing
      ? existing.points.map((point) => ({ ...point }))
      : [];
    draftCurvePoints.value = existing
      ? existing.curvePoints.map((point) => ({ ...point }))
      : [];
    mouseViewPos.value =
      draftPoints.value[draftPoints.value.length - 1] ?? mouseViewPos.value;
    stashedBoundary.value = existing
      ? { stageId: selectedStage.value._id, boundary: existing }
      : null;

    if (existing) {
      const updated = { ...stageBoundaries.value };

      delete updated[selectedStage.value._id];
      stageBoundaries.value = updated;
    }

    isDrawing.value = true;
  };

  const cancelDrawing = () => {
    if (stashedBoundary.value) {
      const { stageId, boundary } = stashedBoundary.value;

      stageBoundaries.value = { ...stageBoundaries.value, [stageId]: boundary };
      stashedBoundary.value = null;
    }

    isDrawing.value = false;
    draftPoints.value = [];
    draftCurvePoints.value = [];
  };

  const addDraftPoint = (e: MouseEvent) => {
    const coords = getSvgCoordsFromEvent(e);

    if (coords) {
      if (draftPoints.value.length >= 3) {
        const closingEdgeIndex = draftPoints.value.length - 1;

        draftCurvePoints.value = draftCurvePoints.value.filter(
          (curve) => curve.edgeIndex !== closingEdgeIndex
        );
      }

      draftPoints.value.push(coords);
    }
  };

  const finishDrawing = () => {
    if (
      !isDrawing.value ||
      !selectedStage.value ||
      draftPoints.value.length < 3
    )
      return;

    const stageId = selectedStage.value._id;
    const points = draftPoints.value.map((point) => ({ ...point }));
    const curvePoints = draftCurvePoints.value.map((point) => ({ ...point }));

    stageBoundaries.value = {
      ...stageBoundaries.value,
      [stageId]: {
        points,
        curvePoints,
        centroid: computeCentroid(points),
      },
    };

    if (!stageLabelMetadata.value[stageId]) {
      stageLabelMetadata.value = {
        ...stageLabelMetadata.value,
        [stageId]: { ...stageLabelAdjustments.value },
      };
    }

    stashedBoundary.value = null;
    isDrawing.value = false;
    draftPoints.value = [];
    draftCurvePoints.value = [];
  };

  const clearBoundary = (stageId: string) => {
    const updated = { ...stageBoundaries.value };

    delete updated[stageId];
    stageBoundaries.value = updated;
  };

  const applyZoom = (newZoom: number, anchorX: number, anchorY: number) => {
    const ratio = newZoom / zoomLevel.value;

    panX.value = anchorX - (anchorX - panX.value) * ratio;
    panY.value = anchorY - (anchorY - panY.value) * ratio;
    zoomLevel.value = newZoom;
  };

  const handleZoomIn = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.min(zoomLevel.value * 1.2, 20), cx, cy);
  };
  const handleZoomOut = () => {
    const container = svgContainer.value;
    const cx = container ? container.clientWidth / 2 : 0;
    const cy = container ? container.clientHeight / 2 : 0;

    applyZoom(Math.max(zoomLevel.value / 1.2, 0.1), cx, cy);
  };
  const handleResetView = () => {
    zoomLevel.value = 1;
    panX.value = 0;
    panY.value = 0;
  };

  const handleWheel = (e: WheelEvent) => {
    const container = svgContainer.value;

    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;

    applyZoom(
      Math.max(0.1, Math.min(20, zoomLevel.value * delta)),
      mouseX,
      mouseY
    );
  };

  const handleMouseDown = (e: MouseEvent) => {
    wasDragging.value = false;
    mouseDownPos.value = { x: e.clientX, y: e.clientY };

    if (e.button === 0) {
      isDragging.value = true;
      dragStart.value = {
        x: e.clientX - panX.value,
        y: e.clientY - panY.value,
      };
    }
  };

  const getPointDistance = (first: Point, second: Point): number =>
    Math.hypot(first.x - second.x, first.y - second.y);

  const getDistanceFromEdge = (
    point: Point,
    start: Point,
    end: Point
  ): number => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) return getPointDistance(point, start);

    const position = Math.max(
      0,
      Math.min(
        1,
        ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared
      )
    );

    return getPointDistance(point, {
      x: start.x + position * dx,
      y: start.y + position * dy,
    });
  };

  const getDraftEdgeHandles = (): Array<CurvePoint & { curved: boolean }> => {
    if (draftPoints.value.length < 3) return [];

    return draftPoints.value.flatMap((point, edgeIndex) => {
      const next =
        draftPoints.value[(edgeIndex + 1) % draftPoints.value.length];
      const curved = draftCurvePoints.value.some(
        (curve) => curve.edgeIndex === edgeIndex
      );

      if (!curved && getPointDistance(point, next) < 22) return [];

      return [
        {
          ...getEdgeMidpoint(
            draftPoints.value,
            draftCurvePoints.value,
            edgeIndex
          ),
          edgeIndex,
          curved,
        },
      ];
    });
  };

  const handleCurveMouseDown = (e: MouseEvent, edgeIndex: number) => {
    e.preventDefault();
    draggingCurveEdgeIndex.value = edgeIndex;
    curveDragStart.value = { x: e.clientX, y: e.clientY };
    curveHandleWasDragged.value = false;
    document.addEventListener("mousemove", handleCurveMouseMove);
    document.addEventListener("mouseup", handleCurveMouseUp);
  };

  const handleCurveMouseMove = (e: MouseEvent) => {
    if (draggingCurveEdgeIndex.value === null) return;

    if (
      Math.hypot(
        e.clientX - curveDragStart.value.x,
        e.clientY - curveDragStart.value.y
      ) >= 3
    ) {
      curveHandleWasDragged.value = true;
    }

    if (!curveHandleWasDragged.value) return;

    const point = getSvgCoordsFromEvent(e);

    if (!point) return;

    const edgeIndex = draggingCurveEdgeIndex.value;
    const start = draftPoints.value[edgeIndex];
    const end = draftPoints.value[(edgeIndex + 1) % draftPoints.value.length];

    if (!start || !end) return;

    const existingIndex = draftCurvePoints.value.findIndex(
      (curve) => curve.edgeIndex === edgeIndex
    );
    const curves = [...draftCurvePoints.value];

    if (getDistanceFromEdge(point, start, end) <= 6) {
      if (existingIndex !== -1) curves.splice(existingIndex, 1);
    } else {
      const curvePoint = { edgeIndex, x: point.x, y: point.y };

      if (existingIndex === -1) curves.push(curvePoint);
      else curves[existingIndex] = curvePoint;
    }

    draftCurvePoints.value = curves;
  };

  const handleCurveMouseUp = () => {
    draggingCurveEdgeIndex.value = null;
    document.removeEventListener("mousemove", handleCurveMouseMove);
    document.removeEventListener("mouseup", handleCurveMouseUp);
  };

  const handleCurveHandleClick = (edgeIndex: number) => {
    if (curveHandleWasDragged.value) {
      curveHandleWasDragged.value = false;
      return;
    }

    handleAddDraftPoint(edgeIndex);
  };

  const handleAddDraftPoint = (edgeIndex: number) => {
    const points = [...draftPoints.value];
    const pointCount = points.length;

    if (pointCount < 3 || edgeIndex < 0 || edgeIndex >= pointCount) return;

    const nextIndex = (edgeIndex + 1) % pointCount;
    const start = points[edgeIndex];
    const end = points[nextIndex];
    const existingCurve = draftCurvePoints.value.find(
      (curve) => curve.edgeIndex === edgeIndex
    );
    const newPoint = getEdgeMidpoint(points, draftCurvePoints.value, edgeIndex);
    const shiftedCurves = draftCurvePoints.value
      .filter((curve) => curve.edgeIndex !== edgeIndex)
      .map((curve) => ({
        ...curve,
        edgeIndex:
          curve.edgeIndex > edgeIndex ? curve.edgeIndex + 1 : curve.edgeIndex,
      }));

    if (existingCurve) {
      const control = getQuadraticControlPoint(start, existingCurve, end);
      const pointOnCurve = (t: number): Point => {
        const inverse = 1 - t;

        return {
          x:
            inverse ** 2 * start.x +
            2 * inverse * t * control.x +
            t ** 2 * end.x,
          y:
            inverse ** 2 * start.y +
            2 * inverse * t * control.y +
            t ** 2 * end.y,
        };
      };

      shiftedCurves.push(
        { edgeIndex, ...pointOnCurve(0.25) },
        { edgeIndex: edgeIndex + 1, ...pointOnCurve(0.75) }
      );
    }

    const insertIndex =
      edgeIndex === pointCount - 1 ? points.length : nextIndex;

    points.splice(insertIndex, 0, newPoint);
    draftPoints.value = points;
    draftCurvePoints.value = shiftedCurves;
  };

  const handleRemoveDraftPoint = (pointIndex: number) => {
    const pointCount = draftPoints.value.length;

    if (pointIndex < 0 || pointIndex >= pointCount) return;

    const previousEdgeIndex = (pointIndex - 1 + pointCount) % pointCount;

    draftCurvePoints.value = draftCurvePoints.value
      .filter(
        (curve) =>
          curve.edgeIndex !== previousEdgeIndex &&
          curve.edgeIndex !== pointIndex
      )
      .map((curve) => ({
        ...curve,
        edgeIndex:
          curve.edgeIndex > pointIndex ? curve.edgeIndex - 1 : curve.edgeIndex,
      }));
    draftPoints.value.splice(pointIndex, 1);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (draggingPointIndex.value >= 0) {
      const coords = getSvgCoordsFromEvent(e);

      if (coords) {
        const updated = [...draftPoints.value];
        const previous = updated[draggingPointIndex.value];
        const delta = { x: coords.x - previous.x, y: coords.y - previous.y };
        const previousEdgeIndex =
          (draggingPointIndex.value - 1 + updated.length) % updated.length;

        draftCurvePoints.value = draftCurvePoints.value.map((curve) =>
          curve.edgeIndex === draggingPointIndex.value ||
          curve.edgeIndex === previousEdgeIndex
            ? {
                ...curve,
                x: curve.x + delta.x / 2,
                y: curve.y + delta.y / 2,
              }
            : curve
        );
        updated[draggingPointIndex.value] = coords;
        draftPoints.value = updated;
      }

      return;
    }

    if (isDragging.value) {
      const dx = e.clientX - mouseDownPos.value.x;
      const dy = e.clientY - mouseDownPos.value.y;

      if (Math.sqrt(dx * dx + dy * dy) > 4) {
        wasDragging.value = true;
      }

      if (wasDragging.value) {
        panX.value = e.clientX - dragStart.value.x;
        panY.value = e.clientY - dragStart.value.y;
      }
    }

    if (isDrawing.value) {
      const coords = getSvgCoordsFromEvent(e);

      if (coords) mouseViewPos.value = coords;
    }
  };

  const handleMouseUp = () => {
    if (draggingPointIndex.value >= 0) {
      draggingPointIndex.value = -1;
      wasPointDragging.value = true;
      return;
    }

    isDragging.value = false;
  };

  const handleContainerClick = (e: MouseEvent) => {
    if (!isDrawing.value) return;

    if (wasDragging.value) return;

    if (wasPointDragging.value) {
      wasPointDragging.value = false;
      return;
    }

    addDraftPoint(e);
  };

  const handleDoubleClick = (e: MouseEvent) => {
    if (isDrawing.value) {
      finishDrawing();
    }
  };

  const handleContextMenu = (e: MouseEvent) => {
    if (!isDrawing.value) return;

    handleRemoveDraftPoint(draftPoints.value.length - 1);
  };

  const handlePointMouseDown = (e: MouseEvent, index: number) => {
    if (!isDrawing.value) return;

    draggingPointIndex.value = index;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isDrawing.value) return;

    if (!(e.target instanceof HTMLElement)) return;

    const target = e.target;

    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT" ||
      target.isContentEditable
    )
      return;

    if (e.key === "Backspace") {
      e.preventDefault();
      handleRemoveDraftPoint(draftPoints.value.length - 1);
    } else if ((e.key === "z" || e.key === "Z") && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleRemoveDraftPoint(draftPoints.value.length - 1);
    }
  };

  const handleComplete = () => {
    const stageAssignments: StageAssignmentResult[] = Object.entries(
      stageBoundaries.value
    ).map(([stageId, boundary]) => ({
      stageId,
      boundaryPath: getBoundaryPath(boundary),
      boundaryPoints: boundary.points.map((point) => ({ ...point })),
      curvePoints: boundary.curvePoints.map((point) => ({ ...point })),
    }));

    const updatedAssignments: EnrichedAssignment[] = props.assignments.map(
      (a) => {
        const item = lotStageItems.value.find(
          (i) => i.elementIndex === a.elementIndex
        );

        return { ...a, stageId: item?.stageId || undefined };
      }
    );

    const stageLabelMetadataArray = Object.entries(
      stageLabelMetadata.value
    ).map(([stageId, meta]) => ({
      stageId,
      ...meta,
    }));

    emit("complete", {
      stageAssignments,
      updatedAssignments,
      stageLabelMetadata: stageLabelMetadataArray,
    });
  };

  // Auto-save stage label adjustments when sliders change
  watch(
    stageLabelAdjustments,
    (newVal) => {
      if (!selectedStage.value || isInitializingLabel.value) return;

      stageLabelMetadata.value = {
        ...stageLabelMetadata.value,
        [selectedStage.value._id]: { ...newVal },
      };
    },
    { deep: true }
  );

  watch(
    stageBoundaries,
    () => {
      measureStageLabelBboxes();
    },
    { deep: true }
  );
  watch(
    stageLabelMetadata,
    () => {
      measureStageLabelBboxes();
    },
    { deep: true }
  );

  const extractViewBox = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent.value, "image/svg+xml");
    const svg = doc.querySelector("svg");

    if (svg) {
      const viewBox = svg.getAttribute("viewBox");

      if (viewBox) {
        overlayViewBox.value = viewBox;
      } else {
        const width = svg.getAttribute("width") || "1000";
        const height = svg.getAttribute("height") || "1000";

        overlayViewBox.value = `0 0 ${width} ${height}`;
      }
    }

    setTimeout(measureStageLabelBboxes, 50);
  };

  onMounted(() => {
    const reader = new FileReader();

    reader.onload = (e) => {
      svgContent.value = e.target?.result as string;
      setTimeout(extractViewBox, 100);
    };
    reader.readAsText(props.svgFile);
    window.addEventListener("keydown", handleKeyDown);
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("mousemove", handleCurveMouseMove);
    document.removeEventListener("mouseup", handleCurveMouseUp);
  });

const __returned__ = { STAGE_COLORS, props, emit, svgContainer, svgOverlay, svgContent, overlayViewBox, stageLabelTextRefs, stageLabelBboxes, stageLabelPadX, stageLabelPadY, stageLabelPillRx, measureStageLabelBboxes, zoomLevel, panX, panY, isDragging, wasDragging, dragStart, mouseDownPos, isDrawing, selectedStage, draftPoints, draftCurvePoints, mouseViewPos, draggingPointIndex, wasPointDragging, draggingCurveEdgeIndex, curveDragStart, curveHandleWasDragged, calcCentroid, buildInitialBoundaries, stageBoundaries, stashedBoundary, buildInitialStageLabelMetadata, stageLabelMetadata, stageLabelAdjustments, isInitializingLabel, svgStyle, hasBoundary, getStageColor, getStageColorById, getStageName, getBoundaryStyle, getBoundaryPath, lotStageItems, drawnBoundariesCount, hexToRgba, computeCentroid, getSvgCoordsFromEvent, stageLabelFontSize, getStageLabelCenterX, getStageLabelCenterY, getStageLabelTransform, getLotNumberCenter, getLotNumberMetadata, shouldShowLotNumber, getLotNumberCenterX, getLotNumberCenterY, getLotNumberTransform, getLotTitle, getLotSizeMetadata, getLotSize, shouldShowLotSize, getLotSizeCenterX, getLotSizeCenterY, getLotSizeTransform, selectStage, selectStageById, startDrawing, cancelDrawing, addDraftPoint, finishDrawing, clearBoundary, applyZoom, handleZoomIn, handleZoomOut, handleResetView, handleWheel, handleMouseDown, getPointDistance, getDistanceFromEdge, getDraftEdgeHandles, handleCurveMouseDown, handleCurveMouseMove, handleCurveMouseUp, handleCurveHandleClick, handleAddDraftPoint, handleRemoveDraftPoint, handleMouseMove, handleMouseUp, handleContainerClick, handleDoubleClick, handleContextMenu, handlePointMouseDown, handleKeyDown, handleComplete, extractViewBox, ref, computed, watch, nextTick, onMounted, onUnmounted, get calculatePolygonCentroid() { return calculatePolygonCentroid }, get calculateBoundingBoxCenter() { return calculateBoundingBoxCenter }, get getLotLabelGap() { return getLotLabelGap }, get LOT_NUMBER_FONT_SIZE() { return LOT_NUMBER_FONT_SIZE }, get LOT_SIZE_FONT_SIZE() { return LOT_SIZE_FONT_SIZE }, get LOT_SIZE_DEFAULT_SCALE() { return LOT_SIZE_DEFAULT_SCALE }, get buildQuadraticShapePath() { return buildQuadraticShapePath }, get convertSvgPathToEditableGeometry() { return convertSvgPathToEditableGeometry }, get getEdgeMidpoint() { return getEdgeMidpoint }, get getQuadraticControlPoint() { return getQuadraticControlPoint } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})